import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Send, Users, RefreshCw, Loader2, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, X, CheckCircle2, XCircle, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_API_URL}/api`;

const TARGET_OPTIONS = [
  { value: "all",   label: "Semua User" },
  { value: "free",  label: "Free Plan" },
  { value: "paid",  label: "Paid Users (Starter/Pro/Business)" },
  { value: "admin", label: "Admin Only" },
];

const STATUS_ICON = {
  sent: <CheckCircle2 className="w-3 h-3 text-verdict-real" />,
  failed: <XCircle className="w-3 h-3 text-red-400" />,
  pending: <Clock className="w-3 h-3 text-amber-400" />,
};

function Pagination({ page, pages, onPage }) {
  if (pages <= 1) return null;
  return (
    <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-border">
      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 rounded-sm" onClick={() => onPage(page - 1)} disabled={page <= 1}><ChevronLeft className="w-3.5 h-3.5" /></Button>
      <span className="text-xs font-mono text-muted-foreground">{page} / {pages}</span>
      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 rounded-sm" onClick={() => onPage(page + 1)} disabled={page >= pages}><ChevronRight className="w-3.5 h-3.5" /></Button>
    </div>
  );
}

function BlastDetailModal({ blast, onClose }) {
  const [filter, setFilter] = useState("all");
  if (!blast) return null;

  const recipients = blast.recipients || [];
  const filtered = filter === "all" ? recipients : recipients.filter(r => r.status === filter);
  const sentCount = recipients.filter(r => r.status === "sent").length;
  const failedCount = recipients.filter(r => r.status === "failed").length;
  const pendingCount = recipients.filter(r => r.status === "pending").length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="blast-detail-modal">
      <div className="bg-background border border-border rounded-md w-full max-w-2xl max-h-[80vh] overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-border">
          <div className="flex-1 min-w-0 pr-4">
            <h3 className="font-heading font-bold text-lg truncate">{blast.subject}</h3>
            <div className="flex items-center gap-3 mt-1.5 text-xs font-mono text-muted-foreground">
              <span className="capitalize">{blast.target}</span>
              <span>-</span>
              <span>{blast.created_at ? new Date(blast.created_at).toLocaleString("id-ID") : "-"}</span>
            </div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1" data-testid="close-blast-detail">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Message preview */}
        <div className="px-5 py-4 border-b border-border bg-secondary/20">
          <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground block mb-2">Pesan</label>
          <div className="text-sm text-foreground whitespace-pre-wrap leading-relaxed max-h-32 overflow-y-auto">{blast.message}</div>
        </div>

        {/* Stats bar */}
        <div className="flex items-center gap-4 px-5 py-3 border-b border-border">
          <span className="text-xs font-mono text-muted-foreground">Total: {recipients.length}</span>
          <button onClick={() => setFilter("all")} className={`text-xs font-mono px-2 py-0.5 rounded-sm transition-colors ${filter === "all" ? "bg-brand-primary/10 text-brand-primary" : "text-muted-foreground hover:text-foreground"}`} data-testid="filter-all">
            Semua
          </button>
          <button onClick={() => setFilter("sent")} className={`text-xs font-mono px-2 py-0.5 rounded-sm transition-colors flex items-center gap-1 ${filter === "sent" ? "bg-verdict-real/10 text-verdict-real" : "text-muted-foreground hover:text-foreground"}`} data-testid="filter-sent">
            {STATUS_ICON.sent} {sentCount} terkirim
          </button>
          <button onClick={() => setFilter("failed")} className={`text-xs font-mono px-2 py-0.5 rounded-sm transition-colors flex items-center gap-1 ${filter === "failed" ? "bg-red-500/10 text-red-400" : "text-muted-foreground hover:text-foreground"}`} data-testid="filter-failed">
            {STATUS_ICON.failed} {failedCount} gagal
          </button>
          {pendingCount > 0 && (
            <button onClick={() => setFilter("pending")} className={`text-xs font-mono px-2 py-0.5 rounded-sm transition-colors flex items-center gap-1 ${filter === "pending" ? "bg-amber-500/10 text-amber-400" : "text-muted-foreground hover:text-foreground"}`} data-testid="filter-pending">
              {STATUS_ICON.pending} {pendingCount} pending
            </button>
          )}
        </div>

        {/* Recipients list */}
        <div className="max-h-[300px] overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">Tidak ada penerima</div>
          ) : (
            <div className="divide-y divide-border">
              {filtered.map((r, i) => (
                <div key={i} className="flex items-center gap-3 px-5 py-2.5 hover:bg-secondary/20 transition-colors">
                  {STATUS_ICON[r.status] || STATUS_ICON.pending}
                  <div className="flex-1 min-w-0">
                    <span className="text-sm truncate block">{r.name || "-"}</span>
                    <span className="text-xs font-mono text-muted-foreground truncate block">{r.email}</span>
                  </div>
                  <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm ${
                    r.status === "sent" ? "bg-verdict-real/10 text-verdict-real" :
                    r.status === "failed" ? "bg-red-500/10 text-red-400" :
                    "bg-amber-500/10 text-amber-400"
                  }`}>{r.status}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminEmailBlast() {
  const { t } = useTranslation();
  const [blast, setBlast]     = useState({ subject: "", message: "", target: "all" });
  const [blasting, setBlasting] = useState(false);
  const [history, setHistory]   = useState([]);
  const [page, setPage]         = useState(1);
  const [detail, setDetail]     = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const PER_PAGE = 10;

  const loadHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/blast-history`, { withCredentials: true });
      setHistory(res.data);
    } catch { }
  }, []);

  const [digestSending, setDigestSending] = useState(false);
  const [digestLogs, setDigestLogs] = useState([]);

  const loadDigestLogs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/digest-log`, { withCredentials: true });
      setDigestLogs(res.data);
    } catch {}
  }, []);

  useEffect(() => { loadHistory(); loadDigestLogs(); }, [loadHistory, loadDigestLogs]);

  const handleSendDigest = async () => {
    setDigestSending(true);
    try {
      await axios.post(`${API}/admin/send-digest`, {}, { withCredentials: true });
      toast.success(t("admin.digest.success"));
      setTimeout(loadDigestLogs, 5000);
    } catch (err) { toast.error(err.response?.data?.detail || t("admin.digest.error")); }
    finally { setDigestSending(false); }
  };

  const handleBlast = async (e) => {
    e.preventDefault();
    if (!blast.subject.trim() || !blast.message.trim()) { toast.error(t("admin.blast.subjectRequired")); return; }
    setBlasting(true);
    try {
      const res = await axios.post(`${API}/admin/blast-email`, blast, { withCredentials: true });
      toast.success(t("admin.blast.sendSuccess", { count: res.data.queued }));
      setBlast({ subject: "", message: "", target: "all" });
      setTimeout(loadHistory, 1500);
    } catch (err) { toast.error(err.response?.data?.detail || t("admin.blast.sendError")); }
    finally { setBlasting(false); }
  };

  const openDetail = async (blastId) => {
    setLoadingDetail(true);
    try {
      const res = await axios.get(`${API}/admin/blast-history/${blastId}`, { withCredentials: true });
      setDetail(res.data);
    } catch { toast.error(t("admin.blast.detailError")); }
    finally { setLoadingDetail(false); }
  };

  const pages = Math.ceil(history.length / PER_PAGE) || 1;
  const paged = history.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  return (
    <div className="p-6 md:p-8" data-testid="admin-email-blast-page">
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-bold">{t("admin.blast.title")}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t("admin.blast.subtitle")}</p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
        {/* Left: Compose Form */}
        <div className="xl:col-span-3">
          <div className="rounded-md border border-border bg-card p-6" data-testid="blast-compose-form">
            <h2 className="text-sm font-mono uppercase tracking-widest text-muted-foreground mb-5">{t("admin.blast.compose")}</h2>
            <form onSubmit={handleBlast} className="space-y-4">
              <div>
                <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground block mb-1.5">Subject</label>
                <Input value={blast.subject} onChange={e => setBlast(p => ({ ...p, subject: e.target.value }))}
                  placeholder="Pengumuman penting dari Veriksa" className="rounded-sm text-sm" data-testid="blast-subject" />
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground block mb-1.5">Pesan</label>
                <textarea value={blast.message} onChange={e => setBlast(p => ({ ...p, message: e.target.value }))}
                  placeholder="Tulis pesan di sini..." rows={8}
                  className="w-full text-sm font-mono bg-background border border-border rounded-sm px-3 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-brand-primary"
                  data-testid="blast-message" />
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground block mb-1.5">
                  <Users className="w-3 h-3 inline mr-1" /> Target Penerima
                </label>
                <div className="grid grid-cols-2 gap-2" data-testid="blast-target-options">
                  {TARGET_OPTIONS.map(opt => (
                    <button key={opt.value} type="button"
                      onClick={() => setBlast(p => ({ ...p, target: opt.value }))}
                      className={`px-3 py-2 rounded-sm border text-sm text-left transition-colors ${
                        blast.target === opt.value
                          ? "border-brand-primary bg-brand-primary/10 text-brand-primary"
                          : "border-border text-muted-foreground hover:border-brand-primary/40"
                      }`}
                      data-testid={`target-${opt.value}`}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="pt-2 flex items-center justify-between">
                <p className="text-xs text-muted-foreground">
                  Sandbox Mailtrap: maks. 1 email/10 detik.<br />
                  Production: gunakan Mailtrap Email Delivery.
                </p>
                <Button type="submit" disabled={blasting} className="rounded-sm bg-brand-primary text-white gap-2 shrink-0" data-testid="blast-send-btn">
                  {blasting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {blasting ? "Mengirim..." : "Kirim"}
                </Button>
              </div>
            </form>
          </div>
        </div>

        {/* Right: History */}
        <div className="xl:col-span-2">
          <div className="rounded-md border border-border bg-card overflow-hidden" data-testid="blast-history-panel">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <h2 className="text-sm font-mono uppercase tracking-widest text-muted-foreground">{t("admin.blast.history")}</h2>
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 rounded-sm" onClick={loadHistory} data-testid="refresh-history-btn">
                <RefreshCw className="w-3.5 h-3.5" />
              </Button>
            </div>
            {paged.length === 0 ? (
              <div className="py-10 text-center text-sm text-muted-foreground">{t("admin.blast.noHistory")}</div>
            ) : (
              <div className="divide-y divide-border">
                {paged.map(b => (
                  <button
                    key={b.blast_id}
                    onClick={() => openDetail(b.blast_id)}
                    className="w-full text-left px-5 py-4 hover:bg-secondary/20 transition-colors group"
                    data-testid={`blast-item-${b.blast_id}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="font-medium text-sm truncate flex-1 group-hover:text-brand-primary transition-colors" title={b.subject}>{b.subject}</div>
                      <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm shrink-0 ${
                        b.status === "complete" ? "bg-verdict-real/10 text-verdict-real" : "bg-amber-500/10 text-amber-400"
                      }`}>{b.status}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs font-mono text-muted-foreground">
                      <span className="capitalize">{b.target}</span>
                      <span>-</span>
                      <span className="text-verdict-real">{b.sent_count} {t("admin.blast.sent")}</span>
                      {b.failed_count > 0 && <><span>-</span><span className="text-red-400">{b.failed_count} {t("admin.blast.failed")}</span></>}
                      <span>-</span>
                      <span>{b.created_at ? new Date(b.created_at).toLocaleDateString("id-ID") : "-"}</span>
                    </div>
                    <div className="text-[10px] text-muted-foreground/50 mt-1 group-hover:text-muted-foreground transition-colors">
                      {t("admin.blast.clickDetail")}
                    </div>
                  </button>
                ))}
              </div>
            )}
            <Pagination page={page} pages={pages} onPage={setPage} />
          </div>
        </div>
      </div>

      {/* Weekly Digest Section */}
      <div className="mt-6 rounded-md border border-border bg-card p-6" data-testid="weekly-digest-section">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-sm font-mono uppercase tracking-widest text-muted-foreground">Weekly Digest</h2>
            <p className="text-xs text-muted-foreground mt-1">Ringkasan mingguan dikirim otomatis setiap Senin 09:00 WIB. Bisa juga trigger manual.</p>
          </div>
          <Button onClick={handleSendDigest} disabled={digestSending} className="rounded-sm bg-brand-primary text-white gap-2 shrink-0" data-testid="send-digest-btn">
            {digestSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {digestSending ? "Mengirim..." : "Kirim Sekarang"}
          </Button>
        </div>
        {digestLogs.length > 0 && (
          <div className="rounded-sm border border-border overflow-hidden" data-testid="digest-log-table">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-secondary/30">
                  {["Tanggal", "Terkirim", "Gagal", "Total User"].map(h => (
                    <th key={h} className="px-4 py-2.5 text-left text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {digestLogs.slice(0, 5).map((log, i) => (
                  <tr key={i} className="border-b border-border last:border-0 hover:bg-secondary/10">
                    <td className="px-4 py-2.5 text-xs font-mono text-muted-foreground">{log.created_at ? new Date(log.created_at).toLocaleString("id-ID") : "-"}</td>
                    <td className="px-4 py-2.5 text-sm font-semibold text-verdict-real">{log.sent_count}</td>
                    <td className="px-4 py-2.5 text-sm font-mono text-red-400">{log.failed_count}</td>
                    <td className="px-4 py-2.5 text-sm font-mono text-muted-foreground">{log.total_users}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {detail && <BlastDetailModal blast={detail} onClose={() => setDetail(null)} />}
      {loadingDetail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <Loader2 className="w-8 h-8 text-brand-primary animate-spin" />
        </div>
      )}
    </div>
  );
}
